import { amountToString } from '@orus.eu/amount'
import { AbstractDimension, deepEqualAssimilateNullUndefined, type TemporaryWakamProblem } from '@orus.eu/dimensions'
import { type MrpwQuoteV2, type MrpwQuoteV3, quoteV3ToSerializedQuoteData } from '@orus.eu/pricing'
import type { Result } from '@orus.eu/result'

import type { FloodRiskProblem } from './wakam-util.ts'

export type MrpwQuoteResult = Result<MrpwQuoteV2, TemporaryWakamProblem | FloodRiskProblem>

export class MrpwQuoteDimension<NAME extends string> extends AbstractDimension<NAME, MrpwQuoteResult> {
  override formatToString(value: MrpwQuoteResult): string {
    if (value.type === 'failure') {
      return `NULL - ${value.problem.type}`
    }
    return JSON.stringify(
      {
        yearlyRecurrenceAmount: amountToString(value.output.yearlyTotalPremium, {
          addCurrency: true,
          displayDecimals: true,
        }),
        monthlyRecurrenceAmount: amountToString(value.output.subsequentMonthsTotalPremium, {
          addCurrency: true,
          displayDecimals: true,
        }),
      },
      null,
      2,
    )
  }
}

export type MrpwQuoteV3Result = Result<MrpwQuoteV3, TemporaryWakamProblem | FloodRiskProblem>

export class MrpwQuoteV3Dimension<NAME extends string> extends AbstractDimension<NAME, MrpwQuoteV3Result> {
  override formatToString(value: MrpwQuoteV3Result): string {
    if (value.type === 'failure') {
      return `NULL - ${value.problem.type}`
    }
    return quoteV3ToSerializedQuoteData(value.output)
  }
  override computedAfterTimestamp = 1751891253000 // Monday, July 7, 2025 2:27:33 PM GMT+02:00 DST

  /**
   * The Wakam API returns a fresh `wakamQuoteReference` and is called at a fresh `wakamApiCalledAt`
   * on every recomputation, even when the actual price and guarantees are unchanged. Ignore these
   * two fields so a daily renewal recomputation is not seen as a real change (INS-1446).
   */
  override areValuesEquivalent(
    a: MrpwQuoteV3Result | null | undefined,
    b: MrpwQuoteV3Result | null | undefined,
  ): boolean {
    return deepEqualAssimilateNullUndefined(stripNonSignificantWakamFields(a), stripNonSignificantWakamFields(b))
  }
}

function stripNonSignificantWakamFields(value: MrpwQuoteV3Result | null | undefined): unknown {
  if (value == undefined || value.type === 'failure') {
    return value
  }
  const {
    wakamQuoteReference: _ignoredReference,
    wakamApiCalledAt: _ignoredCalledAt,
    apiOutputs,
    ...significantInput
  } = value.output.input
  // The same fresh quote reference is also echoed inside each raw Wakam payload, so strip it there too.
  const significantApiOutputs = Object.fromEntries(
    Object.entries(apiOutputs).map(([key, payload]) => [key, stripQuoteReference(payload)]),
  )
  return { ...value, output: { ...value.output, input: { ...significantInput, apiOutputs: significantApiOutputs } } }
}

function stripQuoteReference(payload: { QuoteReference: string } | undefined): unknown {
  if (payload == undefined) return payload
  const { QuoteReference: _ignoredReference, ...significantPayload } = payload
  return significantPayload
}
